import type { User } from "firebase/auth";
import { useCallback, useEffect, useState } from "react";

import { getEnvironment, setTokenProvider } from "../api/client";
import { getIdToken, onAuthChange, signInWithGoogle, signOutUser } from "../auth/firebase";

const ALLOWED_EMAILS = ["piotr1kucharski@gmail.com"];

// Local test-auth seam (build-gated). When VITE_ADMIN_TEST_AUTH === "1", the panel
// skips Firebase entirely and runs as a fixed QA admin against a localhost backend
// whose auth is stubbed too. The comparison is to a literal so vite's `define`
// substitutes `undefined === "1"` → `false` in every shipped build, letting rollup
// dead-code-eliminate this entire branch (and its constants) from the prod bundle —
// it never reaches a real operator.
const TEST_AUTH = import.meta.env.VITE_ADMIN_TEST_AUTH === "1";

function makeTestAdminUser(): User {
  // Minimal shape the panel reads (email for the header, getIdToken for requests).
  // The localhost backend ignores the token (auth is overridden there).
  return {
    uid: "qa-agent-dev",
    email: "qa-agent-dev@voice2mail-gcp.iam.gserviceaccount.com",
    displayName: "QA Agent",
    getIdToken: async () => "test-auth-token",
  } as unknown as User;
}

// Build-gated module-level test user instance — Vite's define substitution replaces
// TEST_AUTH with `false` in every production build, making this null branch dead code
// that Rollup eliminates before shipping.
const TEST_USER = TEST_AUTH ? makeTestAdminUser() : null;

// Fixed QA service-account uid used by the automated dev test harness. It signs
// in via a Firebase custom token, which carries no email claim, so the email
// allowlist above can never match it. Admin is granted to this uid ONLY when the
// panel targets the dev backend (getEnvironment() === "dev"). The deployed panel
// defaults to "prod", so a normal operator never hits this path — it is
// dev-scoped and never reaches the prod allowlist.
const QA_DEV_UID = "qa-agent-dev";

export function isAuthorisedAdmin(user: User): boolean {
  if (user.email && ALLOWED_EMAILS.includes(user.email)) return true;
  return getEnvironment() === "dev" && user.uid === QA_DEV_UID;
}

// ---------------------------------------------------------------------------
// Observability (rule: observability-not-optional) — every sign-in attempt is
// a deterministic, structured signal, GRANTED or DENIED. A denied attempt is
// the interesting one: it means someone who is not the single allowlisted
// operator reached the Google sign-in popup and got matched against
// isAuthorisedAdmin, so it is logged loudly (console.warn) instead of blending
// into routine noise (console.info). There is no backend endpoint to forward
// this to yet — console is the fail-loud channel of record client-side; wiring
// a real alert (e.g. ntfy) requires a backend log-ingest endpoint, which is
// v2m-backend scope, not this panel's.
// ---------------------------------------------------------------------------
export function recordAuthAttempt(user: Pick<User, "email" | "uid">, granted: boolean): void {
  const payload = {
    outcome: granted ? "granted" : "denied",
    email: user.email ?? "(no-email)",
    uid: user.uid,
    environment: getEnvironment(),
    at: new Date().toISOString(),
  };
  if (granted) {
    console.info("[admin-auth]", payload);
  } else {
    console.warn("[admin-auth] ACCESS DENIED", payload);
  }
}

interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
  isAdmin: boolean | null;
}

export function useAuth(): AuthState & {
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
} {
  // Lazy initialiser: when test-auth is active, start with the QA user immediately
  // so there is no extra render that briefly shows an empty/loading state.
  const [state, setState] = useState<AuthState>(() => {
    if (TEST_AUTH && TEST_USER) {
      return { user: TEST_USER, loading: false, error: null, isAdmin: true };
    }
    return { user: null, loading: true, error: null, isAdmin: null };
  });

  useEffect(() => {
    // Build-gated local test-auth: wire up the token provider only; state is already
    // initialised to the QA user by the useState lazy initialiser above.
    if (TEST_AUTH && TEST_USER) {
      setTokenProvider(() => TEST_USER.getIdToken());
      return;
    }

    // Wire up the token provider (getIdToken is already async — signature unchanged).
    setTokenProvider(getIdToken);

    let active = true;
    let unsubscribeInner: (() => void) | undefined;

    // onAuthChange is now async (awaits ensureFirebase() internally).
    // We receive the unsubscribe handle via .then() and store it for cleanup.
    onAuthChange(async (user) => {
      if (!active) return;

      if (!user) {
        setState({ user: null, loading: false, error: null, isAdmin: null });
        return;
      }

      const isAdmin = isAuthorisedAdmin(user);
      recordAuthAttempt(user, isAdmin);

      if (!isAdmin) {
        // Sign out the unauthorised user and show an access-denied message.
        await signOutUser();
        if (!active) return;
        setState({
          user: null,
          loading: false,
          error: "Access Denied. You are not authorized to use this admin panel.",
          isAdmin: false,
        });
        return;
      }

      setState({ user, loading: false, error: null, isAdmin: true });
    })
      .then((unsub) => {
        if (!active) {
          // Component unmounted while Firebase was initialising — unsubscribe immediately.
          unsub();
        } else {
          unsubscribeInner = unsub;
        }
        return;
      })
      .catch((err: unknown) => {
        if (!active) return;
        // Firebase failed to initialise (missing config, bad apiKey, …).
        // Surface a human-readable error in the UI rather than an uncaught throw.
        setState({
          user: null,
          loading: false,
          error:
            err instanceof Error
              ? err.message
              : "Firebase initialisation failed. Check your environment configuration.",
          isAdmin: null,
        });
      });

    return () => {
      active = false;
      unsubscribeInner?.();
    };
  }, []);

  const signIn = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      await signInWithGoogle();
      // Auth state change will trigger the admin check above.
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : "Sign-in failed",
        isAdmin: null,
      }));
    }
  }, []);

  const signOut = useCallback(async () => {
    await signOutUser();
    setState({ user: null, loading: false, error: null, isAdmin: null });
  }, []);

  return { ...state, signIn, signOut };
}
